// =========================
// lib/admin.js
//
// Data layer for the Admin Portal. Bridges AdminDashboard
// to the two admin Netlify functions:
//   admin-get-source-config   (read)
//   admin-update-source-config (write)
//
// Both functions get the current Supabase session JWT
// and pass it as a Bearer token. Fail-closed: if no
// active session exists, returns { error: 'Not signed in' }
// without making any network call.
//
// URL convention: ${API_BASE_URL}/<function-name>. API_BASE_URL
// resolves to http://localhost:8888/api in dev (netlify.toml
// rewrites /api/* → /.netlify/functions/*). Do NOT include
// /.netlify/functions in the path here — that would double the
// prefix at runtime (api/.netlify/functions/...). Matches the
// pattern used by every other Netlify-function caller in the
// codebase (DevSubscriptionPanel, AuthContext, etc).
// =========================

import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

export async function listSourceConfig() {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-get-source-config`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body.data, error: null };
}

export async function updateSourceConfig({ source_key, enabled, priority }) {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-update-source-config`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
    body: JSON.stringify({ source_key, enabled, priority }),
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body, error: null };
}

// --- User Management panel (Workstream 4, Phase 2) -----------------------

// Returns { data: users[], error } — one row per user, merged from Supabase
// Auth (email, created_at) + profiles (tier, status, trial). Rows come from
// body.users, mirroring listSourceConfig's body.data convention.
export async function listUsers() {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-list-users`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body.users, error: null };
}

// Manual tier override. Returns the full flat response body as data
// ({ success, user_id, tier }), matching updateSourceConfig's convention.
export async function setUserTier({ user_id, tier }) {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-set-user-tier`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
    body: JSON.stringify({ user_id, tier }),
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body, error: null };
}

// --- Feature Flags panel (Workstream 4, Phase 2) -------------------------

// Returns { data: rows[], error } — all feature_flags rows ordered by
// flag_key. Rows come from body.data, mirroring listSourceConfig.
export async function listFeatureFlags() {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-get-feature-flags`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body.data, error: null };
}

// Toggle a flag and/or update its tier scope. Returns the full flat response
// body as data ({ success, flag_key, enabled }), matching updateSourceConfig.
export async function updateFeatureFlag({ flag_key, enabled, enabled_for_tiers }) {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-update-feature-flag`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
    body: JSON.stringify({ flag_key, enabled, enabled_for_tiers }),
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body, error: null };
}

// --- Feature Management panel (PLATFORM-003) -----------------------------

// Read-only registry view: every feature with a server-computed enablement
// evaluation attached ({ enablement: { allowed, reasons } }). Rows come from
// body.data, mirroring listFeatureFlags. The server is the enablement authority;
// the panel only DISPLAYS enablement.reasons as warnings.
export async function listFeatureRegistry() {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-get-feature-registry`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body.data, error: null };
}

// Enable/disable a feature at runtime. The server enforces the Release → Verify
// → Enable preconditions and returns 409 { code:'enablement_blocked', reasons }
// when they are unmet; that body is surfaced verbatim as the error so the panel
// can show why. Returns the flat body as data on success ({ success, flag_key,
// enabled }).
export async function setFeatureState({ flag_key, enabled, reason }) {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-set-feature-state`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
    body: JSON.stringify({ flag_key, enabled, reason }),
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: {
      message: body?.error || `HTTP ${response.status}`,
      reasons: body?.reasons || null,
      code: body?.code || null,
    },
  };
  return { data: body, error: null };
}

// Read the append-only audit history, newest first. Optionally scoped to one
// feature via { flag_key }. Rows come from body.data.
export async function listFeatureAudit({ flag_key } = {}) {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-get-feature-audit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
    body: JSON.stringify({ flag_key }),
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body.data, error: null };
}

// --- Audience visibility panel (BL-018b) ---------------------------------

// Read-only durable-audience summary for the Admin Portal audience panel.
// Returns { data: { total, recent, composition }, error } — the summary comes
// from body.data, mirroring listSourceConfig / listFeatureFlags. NOT a CRM:
// no editing, contacting, or scoring (BL-018b §6 exclusions).
export async function listWaitlist() {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-get-waitlist`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body.data, error: null };
}

// Send a controlled-onboarding invitation. Records a verified "invitation was
// sent" fact in the Product-owned Invitation Aggregate; the server publishes the
// participant.invitation_sent Domain Event (PLATFORM-006 / ADR-064). This is
// OBSERVABILITY, not authorization — recording an invitation does NOT admit
// anyone (the frontend allowlist remains the enforcement mechanism). Returns the
// flat body as data ({ success, invitation }), matching updateSourceConfig.
export async function sendInvitation({ email, audience, provider } = {}) {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-send-invitation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
    body: JSON.stringify({ email, audience, provider }),
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body, error: null };
}

// --- Funnel visibility panel (BL-038 Story 38.6) -------------------------

// Read-only AGGREGATE conversion-funnel metrics for the Admin Portal funnel
// panel: waitlist size, plan-intent distribution, source attribution, and
// trial/activation counts. Returns { data: metrics, error } — the metrics come
// from body.metrics (matching admin-get-trial-metrics). No PII, no payment data.
export async function listFunnelMetrics() {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-get-funnel-metrics`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body.metrics, error: null };
}

// --- Support inbox panel (BL-037 Story 37.1) -----------------------------

// Read-only support-submission summary for the Admin Portal support panel.
// Returns { data: { total, recent, byCategory, byStatus }, error } — the
// summary comes from body.data, mirroring listWaitlist. Read-only OBSERVATION:
// no replying or status mutation (status workflow is Story 37.3).
export async function listSupportRequests() {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-list-support-requests`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body.data, error: null };
}

// Advance a support request's status through the enforced workflow
// (new → reviewing → closed; closed → reviewing). The server validates the
// transition against the row's current status; this client just posts the
// target. Returns the full flat response body as data ({ success, id, status }),
// matching setUserTier / updateFeatureFlag. Story 37.3.
export async function updateSupportRequest({ id, status }) {
  const token = await getAccessToken();
  if (!token) return { data: null, error: { message: 'Not signed in' } };
  const response = await fetch(`${API_BASE_URL}/admin-update-support-request`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization:  `Bearer ${token}`,
    },
    body: JSON.stringify({ id, status }),
  });
  const body = await response.json();
  if (!response.ok) return {
    data: null,
    error: { message: body?.error || `HTTP ${response.status}` },
  };
  return { data: body, error: null };
}
